import React from 'react';
import { Building2, Store, MapPin } from 'lucide-react';
import type { LocationQuantity } from '../../utils/locationInventory';

interface LocationQuantityDisplayProps {
  quantities: LocationQuantity[];
  compact?: boolean;
  showZero?: boolean;
}

const getLocationIcon = (locationName: string) => {
  const name = locationName.toLowerCase();
  if (name === 'warehouse' || name.includes('warehouse')) {
    return Building2;
  }
  if (name === 'shop' || name.includes('shop') || name.includes('store')) {
    return Store;
  }
  return MapPin;
};

const getLocationColor = (locationName: string, quantity: number) => {
  if (quantity === 0) {
    return 'text-gray-400 dark:text-gray-500';
  }
  const name = locationName.toLowerCase();
  if (name === 'warehouse' || name.includes('warehouse')) {
    return 'text-blue-600 dark:text-blue-400';
  }
  if (name === 'shop' || name.includes('shop') || name.includes('store')) {
    return 'text-emerald-600 dark:text-emerald-400';
  }
  return 'text-amber-600 dark:text-amber-400';
};

export const LocationQuantityDisplay: React.FC<LocationQuantityDisplayProps> = ({
  quantities,
  compact = false,
  showZero = false
}) => {
  const displayQuantities = showZero
    ? quantities
    : quantities.filter(q => q.quantity > 0);

  if (displayQuantities.length === 0) {
    return (
      <span className="text-gray-400 dark:text-gray-500 text-xs">
        No stock
      </span>
    );
  }

  if (compact) {
    return (
      <div className="flex flex-wrap gap-1">
        {displayQuantities.map((q) => {
          const Icon = getLocationIcon(q.locationName);
          const colorClass = getLocationColor(q.locationName, q.quantity);
          return (
            <span
              key={q.locationId}
              className="inline-flex items-center gap-1 text-xs"
              title={`${q.locationName}: ${q.quantity}`}
            >
              <Icon className={`h-3 w-3 ${colorClass}`} />
              <span className={q.quantity > 0 ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}>
                {q.quantity}
              </span>
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {displayQuantities.map((q) => {
        const Icon = getLocationIcon(q.locationName);
        const colorClass = getLocationColor(q.locationName, q.quantity);
        return (
          <div
            key={q.locationId}
            className="flex items-center gap-1.5 text-xs"
          >
            <Icon className={`h-3 w-3 flex-shrink-0 ${colorClass}`} />
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[80px]">
              {q.locationName}:
            </span>
            <span className={`font-medium ${q.quantity > 0 ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
              {q.quantity}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default LocationQuantityDisplay;
